export const DESIGN_PRESET_STORAGE_KEY = 'bes-ui-design-preset';
export const DEFAULT_DESIGN_PRESET = 'formalNeutral';

export const designPresets = {
  formalNeutral: createColorTheme({
    id: 'formalNeutral',
    label: 'Invoice Studio',
    description: 'A polished teal-and-ink light palette for the full billing workspace.',
    mode: 'light',
    preview: {
      hero: '#f4fbfc',
      surface: '#ffffff',
      accent: '#0f758e',
      accentSoft: '#d8f4f8',
    },
    fontDisplay: "'Segoe UI', 'Aptos', 'Helvetica Neue', sans-serif",
    fontBody: "'Segoe UI', 'Aptos', 'Helvetica Neue', sans-serif",
    bgBase: '#f4fbfc',
    surface1: '#ffffff',
    surface2: '#eef7fa',
    surface3: '#dff1f5',
    textMain: '#1f2430',
    textSoft: '#5e6478',
    textSubtle: '#7d91a0',
    brand: '#0f758e',
    brandStrong: '#0b5a70',
    accent: '#2ca3b8',
    accentSoft: '#d8f4f8',
    success: '#52c41a',
    warning: '#faad14',
    danger: '#ff4d4f',
    borderRadius: 14,
  }),
  formalDark: {
    id: 'formalDark',
    label: 'Dark Professional',
    description: 'A graphite, brass, and steel dark palette for higher-contrast operator workflows.',
    mode: 'dark',
    preview: {
      hero: '#1d2730',
      surface: '#11171d',
      accent: '#c7a36b',
      accentSoft: '#32414c',
    },
    antd: {
      colorPrimary: '#c7a36b',
      colorSuccess: '#5da884',
      colorWarning: '#d59c57',
      colorError: '#d97469',
      colorBgBase: '#0f1418',
      colorTextBase: '#edf1f4',
      colorBorder: '#35414c',
      fontFamily: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
      borderRadius: 14,
    },
    cssVars: {
      '--font-display': "'Georgia', 'Cambria', 'Times New Roman', serif",
      '--font-body': "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
      '--surface-1': 'rgba(17, 23, 29, 0.96)',
      '--surface-2': 'rgba(24, 31, 38, 0.96)',
      '--surface-3': 'rgba(35, 45, 54, 0.88)',
      '--text-main': '#edf1f4',
      '--text-soft': '#b8c2cc',
      '--text-subtle': '#98a4af',
      '--brand': '#c7a36b',
      '--brand-strong': '#e0bf8d',
      '--accent': '#7ab0c6',
      '--accent-soft': '#2b3843',
      '--success': '#5da884',
      '--warning': '#d59c57',
      '--danger': '#d97469',
      '--shadow-soft': '0 18px 42px rgba(0, 0, 0, 0.34)',
      '--shadow-elevated': '0 28px 58px rgba(0, 0, 0, 0.42)',
      '--app-bg-base': '#0b1014',
      '--app-bg-orb-1': 'rgba(54, 73, 88, 0.56)',
      '--app-bg-orb-2': 'rgba(199, 163, 107, 0.14)',
      '--selection-bg': 'rgba(199, 163, 107, 0.28)',
      '--selection-text': '#edf1f4',
      '--shell-surface': 'rgba(14, 19, 24, 0.96)',
      '--shell-border': 'rgba(151, 169, 184, 0.14)',
      '--shell-content-bg-start': 'rgba(17, 23, 29, 0.98)',
      '--shell-content-bg-end': 'rgba(25, 32, 40, 0.96)',
      '--shell-banner-bg': 'linear-gradient(120deg, rgba(27, 36, 45, 0.94), rgba(22, 28, 34, 0.96))',
      '--shell-banner-border': 'rgba(199, 163, 107, 0.22)',
      '--keyboard-banner-bg': 'linear-gradient(118deg, rgba(31, 40, 50, 0.94), rgba(20, 27, 33, 0.96))',
      '--keyboard-banner-border': 'rgba(199, 163, 107, 0.26)',
      '--section-kicker': '#e0bf8d',
      '--sidebar-bg': 'linear-gradient(180deg, rgba(14, 19, 24, 0.96), rgba(19, 25, 31, 0.96))',
      '--sidebar-border': 'rgba(151, 169, 184, 0.14)',
      '--sidebar-fg': '#edf1f4',
      '--sidebar-muted': '#a9b4be',
      '--sidebar-surface': 'rgba(255, 255, 255, 0.04)',
      '--sidebar-surface-border': 'rgba(151, 169, 184, 0.12)',
      '--sidebar-icon-bg': 'rgba(255, 255, 255, 0.06)',
      '--sidebar-action-bg': 'rgba(255, 255, 255, 0.04)',
      '--sidebar-action-border': 'rgba(151, 169, 184, 0.14)',
      '--nav-selected-bg': 'linear-gradient(100deg, rgba(199, 163, 107, 0.16), rgba(122, 176, 198, 0.1))',
      '--nav-selected-border': 'rgba(199, 163, 107, 0.22)',
      '--topbar-bg': 'linear-gradient(90deg, rgba(15, 21, 27, 0.9), rgba(20, 27, 33, 0.92))',
      '--topbar-border': 'rgba(151, 169, 184, 0.14)',
      '--topbar-search-bg': 'rgba(24, 31, 38, 0.92)',
      '--topbar-search-border': 'rgba(151, 169, 184, 0.18)',
      '--topbar-control-bg': 'rgba(24, 31, 38, 0.9)',
      '--topbar-control-border': 'rgba(151, 169, 184, 0.18)',
      '--shell-pill-bg': 'rgba(24, 31, 38, 0.88)',
      '--shell-pill-border': 'rgba(151, 169, 184, 0.18)',
      '--shell-key-bg': '#222c35',
      '--shell-key-border': 'rgba(151, 169, 184, 0.2)',
      '--mode-tag-bg': 'rgba(199, 163, 107, 0.14)',
      '--mode-tag-border': 'rgba(199, 163, 107, 0.24)',
      '--assist-bg': 'linear-gradient(180deg, rgba(16, 21, 27, 0.96), rgba(21, 28, 34, 0.96))',
      '--assist-tag-bg': 'rgba(199, 163, 107, 0.12)',
      '--assist-tag-border': 'rgba(199, 163, 107, 0.22)',
      '--table-head-bg': 'rgba(33, 43, 52, 0.96)',
      '--table-head-bg-2': 'rgba(23, 30, 36, 0.96)',
      '--table-surface': 'rgba(17, 23, 29, 0.98)',
      '--table-surface-alt': 'rgba(24, 31, 38, 0.94)',
      '--table-row-hover': 'rgba(41, 53, 63, 0.42)',
      '--table-active-border': 'rgba(199, 163, 107, 0.45)',
      '--workspace-stat-bg': 'linear-gradient(145deg, rgba(17, 23, 29, 0.98), rgba(28, 36, 44, 0.96))',
      '--workspace-stat-accent-bg': 'linear-gradient(145deg, rgba(36, 46, 56, 0.96), rgba(17, 23, 29, 0.98))',
      '--workspace-toolbar-bg': 'rgba(18, 24, 30, 0.9)',
      '--workspace-panel-bg-start': 'rgba(17, 23, 29, 0.98)',
      '--workspace-panel-bg-end': 'rgba(25, 32, 40, 0.96)',
      '--workspace-aside-bg': 'rgba(36, 46, 56, 0.64)',
      '--workspace-aside-border': 'rgba(199, 163, 107, 0.16)',
      '--workspace-keyboard-hero-bg': 'linear-gradient(135deg, rgba(33, 43, 52, 0.96), rgba(20, 27, 33, 0.96))',
      '--workspace-keyboard-hero-border': 'rgba(199, 163, 107, 0.2)',
      '--workspace-keyboard-pill-bg': 'rgba(21, 28, 34, 0.92)',
      '--workspace-keyboard-pill-border': 'rgba(151, 169, 184, 0.18)',
      '--workspace-keyboard-key-bg': '#232d36',
      '--workspace-keyboard-key-border': 'rgba(151, 169, 184, 0.2)',
      '--workspace-keyboard-item-bg': 'rgba(20, 27, 33, 0.92)',
      '--workspace-keyboard-item-active-bg': 'linear-gradient(140deg, rgba(44, 56, 67, 0.94), rgba(20, 27, 33, 0.98))',
      '--workspace-keyboard-item-active-border': 'rgba(199, 163, 107, 0.3)',
      '--workspace-keyboard-item-active-ring': 'rgba(199, 163, 107, 0.12)',
      '--workspace-icon-bg': 'rgba(199, 163, 107, 0.12)',
      '--workspace-meta-pill-bg': 'rgba(28, 36, 44, 0.96)',
      '--workspace-meta-pill-border': 'rgba(151, 169, 184, 0.18)',
      '--document-meta-bg': 'linear-gradient(135deg, rgba(40, 51, 61, 0.9), rgba(17, 23, 29, 0.98))',
      '--document-meta-border': 'rgba(199, 163, 107, 0.16)',
      '--document-section-border': 'rgba(151, 169, 184, 0.14)',
      '--document-section-bg-start': 'rgba(18, 24, 30, 0.98)',
      '--document-section-bg-end': 'rgba(26, 34, 42, 0.96)',
      '--document-preview-bg': 'linear-gradient(135deg, rgba(40, 51, 61, 0.84), rgba(17, 23, 29, 0.94))',
      '--document-preview-border': 'rgba(199, 163, 107, 0.16)',
      '--document-preview-label': '#94a0ac',
      '--document-item-bg-start': 'rgba(24, 31, 38, 0.96)',
      '--document-item-bg-end': 'rgba(17, 23, 29, 0.98)',
      '--document-item-border': 'rgba(151, 169, 184, 0.14)',
      '--document-neutral-card-bg': 'rgba(22, 28, 34, 0.96)',
      '--document-neutral-card-border': 'rgba(151, 169, 184, 0.14)',
      '--document-chip-bg': 'rgba(199, 163, 107, 0.12)',
      '--document-chip-border': 'rgba(199, 163, 107, 0.18)',
      '--document-add-bg': 'rgba(199, 163, 107, 0.08)',
      '--document-summary-hero-bg': 'linear-gradient(135deg, rgba(42, 71, 60, 0.64), rgba(44, 56, 67, 0.74))',
      '--document-summary-hero-border': 'rgba(93, 168, 132, 0.18)',
      '--document-summary-row-border': 'rgba(151, 169, 184, 0.1)',
      '--editor-band-border': 'rgba(151, 169, 184, 0.14)',
      '--editor-band-main-bg': 'radial-gradient(circle at top right, rgba(199, 163, 107, 0.16), transparent 36%), linear-gradient(135deg, rgba(24, 31, 38, 0.98), rgba(17, 23, 29, 0.98))',
      '--editor-band-aside-bg': 'linear-gradient(180deg, rgba(24, 31, 38, 0.98), rgba(17, 23, 29, 0.98))',
      '--editor-chip-bg': 'rgba(24, 31, 38, 0.92)',
      '--editor-chip-border': 'rgba(199, 163, 107, 0.18)',
      '--editor-chip-text': '#e0bf8d',
      '--editor-field-cluster-bg': 'rgba(24, 31, 38, 0.88)',
      '--editor-field-cluster-border': 'rgba(151, 169, 184, 0.14)',
      '--editor-board-bg': 'rgba(17, 23, 29, 0.98)',
      '--editor-board-border': 'rgba(151, 169, 184, 0.14)',
      '--editor-board-head-bg': 'linear-gradient(135deg, rgba(13, 18, 23, 0.98), rgba(24, 31, 38, 0.98))',
      '--editor-board-head-text': '#edf1f4',
      '--editor-mobile-bar-bg': 'rgba(17, 23, 29, 0.94)',
      '--editor-mobile-bar-border': 'rgba(151, 169, 184, 0.14)',
      '--primary-action-bg': 'linear-gradient(135deg, #b79259, #c7a36b)',
      '--primary-action-hover-bg': 'linear-gradient(135deg, #a88651, #b79259)',
      '--switch-handle-bg': '#edf1f4',
    },
  },
  playfulAccent: {
    id: 'playfulAccent',
    label: 'Playful Accent',
    description: 'A warm coral-and-sky light palette with softer paper surfaces and brighter highlights.',
    mode: 'light',
    preview: {
      hero: '#ffe2d6',
      surface: '#fffdf8',
      accent: '#e56b5d',
      accentSoft: '#b8daf3',
    },
    antd: {
      colorPrimary: '#e56b5d',
      colorSuccess: '#2f8f75',
      colorWarning: '#dd8a2b',
      colorError: '#cb4545',
      colorBgBase: '#fff7f1',
      colorTextBase: '#2b2f38',
      colorBorder: '#f0c8ba',
      fontFamily: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
      borderRadius: 16,
    },
    cssVars: {
      '--font-display': "'Trebuchet MS', 'Aptos Display', 'Segoe UI', sans-serif",
      '--font-body': "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
      '--surface-1': 'rgba(255, 253, 248, 0.96)',
      '--surface-2': 'rgba(255, 244, 236, 0.96)',
      '--surface-3': 'rgba(230, 244, 251, 0.86)',
      '--text-main': '#2b2f38',
      '--text-soft': '#5d6575',
      '--text-subtle': '#727b8c',
      '--brand': '#e56b5d',
      '--brand-strong': '#c24d40',
      '--accent': '#3b82c4',
      '--accent-soft': '#dbefff',
      '--success': '#2f8f75',
      '--warning': '#dd8a2b',
      '--danger': '#cb4545',
      '--shadow-soft': '0 16px 36px rgba(91, 68, 62, 0.12)',
      '--shadow-elevated': '0 24px 48px rgba(91, 68, 62, 0.16)',
      '--app-bg-base': '#fff8f2',
      '--app-bg-orb-1': 'rgba(255, 198, 175, 0.44)',
      '--app-bg-orb-2': 'rgba(184, 218, 243, 0.34)',
      '--selection-bg': 'rgba(229, 107, 93, 0.18)',
      '--selection-text': '#2b2f38',
      '--shell-surface': 'rgba(255, 253, 248, 0.96)',
      '--shell-border': 'rgba(198, 105, 94, 0.12)',
      '--shell-content-bg-start': 'rgba(255, 253, 248, 0.98)',
      '--shell-content-bg-end': 'rgba(255, 245, 238, 0.96)',
      '--shell-banner-bg': 'linear-gradient(120deg, rgba(255, 226, 214, 0.92), rgba(229, 244, 252, 0.92))',
      '--shell-banner-border': 'rgba(229, 107, 93, 0.18)',
      '--keyboard-banner-bg': 'linear-gradient(118deg, rgba(255, 232, 222, 0.94), rgba(219, 239, 255, 0.92))',
      '--keyboard-banner-border': 'rgba(59, 130, 196, 0.22)',
      '--section-kicker': '#c24d40',
      '--sidebar-bg': 'linear-gradient(180deg, rgba(255, 251, 246, 0.94), rgba(255, 239, 228, 0.94))',
      '--sidebar-border': 'rgba(198, 105, 94, 0.12)',
      '--sidebar-fg': '#2b2f38',
      '--sidebar-muted': '#6e7685',
      '--sidebar-surface': 'rgba(255, 255, 255, 0.58)',
      '--sidebar-surface-border': 'rgba(198, 105, 94, 0.12)',
      '--sidebar-icon-bg': 'rgba(255, 255, 255, 0.72)',
      '--sidebar-action-bg': 'rgba(255, 255, 255, 0.74)',
      '--sidebar-action-border': 'rgba(198, 105, 94, 0.14)',
      '--nav-selected-bg': 'linear-gradient(100deg, rgba(229, 107, 93, 0.16), rgba(59, 130, 196, 0.12))',
      '--nav-selected-border': 'rgba(229, 107, 93, 0.2)',
      '--topbar-bg': 'linear-gradient(90deg, rgba(255, 253, 248, 0.9), rgba(255, 239, 228, 0.9))',
      '--topbar-border': 'rgba(198, 105, 94, 0.12)',
      '--topbar-search-bg': 'rgba(255, 255, 255, 0.9)',
      '--topbar-search-border': 'rgba(198, 105, 94, 0.14)',
      '--topbar-control-bg': 'rgba(255, 255, 255, 0.76)',
      '--topbar-control-border': 'rgba(198, 105, 94, 0.14)',
      '--shell-pill-bg': 'rgba(255, 252, 247, 0.86)',
      '--shell-pill-border': 'rgba(198, 105, 94, 0.14)',
      '--shell-key-bg': '#fff2e8',
      '--shell-key-border': 'rgba(198, 105, 94, 0.2)',
      '--mode-tag-bg': 'rgba(229, 107, 93, 0.1)',
      '--mode-tag-border': 'rgba(229, 107, 93, 0.22)',
      '--assist-bg': 'linear-gradient(180deg, rgba(255, 251, 246, 0.94), rgba(255, 239, 228, 0.94))',
      '--assist-tag-bg': 'rgba(229, 107, 93, 0.08)',
      '--assist-tag-border': 'rgba(229, 107, 93, 0.18)',
      '--table-head-bg': 'rgba(255, 229, 216, 0.86)',
      '--table-head-bg-2': 'rgba(229, 244, 252, 0.94)',
      '--table-surface': 'rgba(255, 253, 248, 0.98)',
      '--table-surface-alt': 'rgba(255, 244, 236, 0.86)',
      '--table-row-hover': 'rgba(255, 232, 222, 0.42)',
      '--table-active-border': 'rgba(229, 107, 93, 0.36)',
      '--workspace-stat-bg': 'linear-gradient(145deg, rgba(255, 253, 248, 0.98), rgba(255, 239, 228, 0.94))',
      '--workspace-stat-accent-bg': 'linear-gradient(145deg, rgba(255, 226, 214, 0.92), rgba(255, 253, 248, 0.98))',
      '--workspace-toolbar-bg': 'rgba(255, 251, 246, 0.84)',
      '--workspace-panel-bg-start': 'rgba(255, 253, 248, 0.98)',
      '--workspace-panel-bg-end': 'rgba(255, 245, 238, 0.94)',
      '--workspace-aside-bg': 'rgba(255, 232, 222, 0.56)',
      '--workspace-aside-border': 'rgba(229, 107, 93, 0.14)',
      '--workspace-keyboard-hero-bg': 'linear-gradient(135deg, rgba(255, 232, 222, 0.94), rgba(229, 244, 252, 0.96))',
      '--workspace-keyboard-hero-border': 'rgba(59, 130, 196, 0.18)',
      '--workspace-keyboard-pill-bg': 'rgba(255, 252, 247, 0.9)',
      '--workspace-keyboard-pill-border': 'rgba(198, 105, 94, 0.14)',
      '--workspace-keyboard-key-bg': '#fff2e8',
      '--workspace-keyboard-key-border': 'rgba(198, 105, 94, 0.18)',
      '--workspace-keyboard-item-bg': 'rgba(255, 251, 246, 0.9)',
      '--workspace-keyboard-item-active-bg': 'linear-gradient(140deg, rgba(255, 226, 214, 0.9), rgba(255, 253, 248, 0.98))',
      '--workspace-keyboard-item-active-border': 'rgba(229, 107, 93, 0.26)',
      '--workspace-keyboard-item-active-ring': 'rgba(229, 107, 93, 0.08)',
      '--workspace-icon-bg': 'rgba(59, 130, 196, 0.1)',
      '--workspace-meta-pill-bg': 'rgba(255, 245, 238, 0.94)',
      '--workspace-meta-pill-border': 'rgba(198, 105, 94, 0.12)',
      '--document-meta-bg': 'linear-gradient(135deg, rgba(255, 226, 214, 0.86), rgba(255, 253, 248, 0.96))',
      '--document-meta-border': 'rgba(229, 107, 93, 0.16)',
      '--document-section-border': 'rgba(198, 105, 94, 0.1)',
      '--document-section-bg-start': 'rgba(255, 253, 248, 0.98)',
      '--document-section-bg-end': 'rgba(255, 245, 238, 0.96)',
      '--document-preview-bg': 'linear-gradient(135deg, rgba(255, 226, 214, 0.82), rgba(229, 244, 252, 0.9))',
      '--document-preview-border': 'rgba(229, 107, 93, 0.16)',
      '--document-preview-label': '#7c8696',
      '--document-item-bg-start': 'rgba(255, 245, 238, 0.96)',
      '--document-item-bg-end': 'rgba(255, 253, 248, 0.98)',
      '--document-item-border': 'rgba(198, 105, 94, 0.1)',
      '--document-neutral-card-bg': 'rgba(255, 251, 246, 0.96)',
      '--document-neutral-card-border': 'rgba(198, 105, 94, 0.1)',
      '--document-chip-bg': 'rgba(219, 239, 255, 0.76)',
      '--document-chip-border': 'rgba(59, 130, 196, 0.16)',
      '--document-add-bg': 'rgba(255, 226, 214, 0.3)',
      '--document-summary-hero-bg': 'linear-gradient(135deg, rgba(255, 232, 222, 0.86), rgba(219, 239, 255, 0.84))',
      '--document-summary-hero-border': 'rgba(47, 143, 117, 0.16)',
      '--document-summary-row-border': 'rgba(198, 105, 94, 0.08)',
      '--editor-band-border': 'rgba(198, 105, 94, 0.12)',
      '--editor-band-main-bg': 'radial-gradient(circle at top right, rgba(59, 130, 196, 0.16), transparent 32%), linear-gradient(135deg, rgba(255, 253, 248, 0.98), rgba(255, 232, 222, 0.94))',
      '--editor-band-aside-bg': 'linear-gradient(180deg, rgba(255, 253, 248, 0.98), rgba(255, 245, 238, 0.94))',
      '--editor-chip-bg': 'rgba(255, 255, 255, 0.86)',
      '--editor-chip-border': 'rgba(229, 107, 93, 0.18)',
      '--editor-chip-text': '#c24d40',
      '--editor-field-cluster-bg': 'rgba(255, 245, 238, 0.82)',
      '--editor-field-cluster-border': 'rgba(198, 105, 94, 0.1)',
      '--editor-board-bg': 'rgba(255, 251, 246, 0.96)',
      '--editor-board-border': 'rgba(198, 105, 94, 0.1)',
      '--editor-board-head-bg': 'linear-gradient(135deg, #2b2f38, #4d5564)',
      '--editor-board-head-text': '#fffdf8',
      '--editor-mobile-bar-bg': 'rgba(255, 251, 246, 0.94)',
      '--editor-mobile-bar-border': 'rgba(198, 105, 94, 0.12)',
      '--primary-action-bg': 'linear-gradient(135deg, #e56b5d, #f08a52)',
      '--primary-action-hover-bg': 'linear-gradient(135deg, #c24d40, #dd8a2b)',
      '--switch-handle-bg': '#fffdf8',
    },
  },
};

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalized;

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mix(colorA, colorB, weight) {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  const mixChannel = (channelA, channelB) =>
    Math.round(channelA + (channelB - channelA) * weight)
      .toString(16)
      .padStart(2, '0');

  return `#${mixChannel(a.r, b.r)}${mixChannel(a.g, b.g)}${mixChannel(a.b, b.b)}`;
}

function gradient(angle, ...stops) {
  return `linear-gradient(${angle}, ${stops.join(', ')})`;
}

function luminanceFromHex(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return 1;
  }

  return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
}

function ensureDarkTone(hex, maxLuminance = 0.26) {
  if (luminanceFromHex(hex) <= maxLuminance) {
    return hex;
  }

  let weight = 0.06;
  let candidate = mix(hex, '#0a1018', weight);

  while (luminanceFromHex(candidate) > maxLuminance && weight < 0.92) {
    weight += 0.06;
    candidate = mix(hex, '#0a1018', weight);
  }

  return candidate;
}

function buildNavigationToneTokens({ mode, cssVars, antd }) {
  const brand = cssVars['--brand'] || antd.colorPrimary || '#7c3aed';
  const accent = cssVars['--accent'] || brand;
  const bgBase = cssVars['--app-bg-base'] || antd.colorBgBase || '#111827';
  const isDark = mode === 'dark';
  const darkShellStart = ensureDarkTone(
    isDark ? mix(bgBase, brand, 0.16) : mix('#0b1020', brand, 0.18),
    0.25
  );
  const darkShellEnd = ensureDarkTone(
    isDark ? mix(bgBase, accent, 0.2) : mix('#141b2d', accent, 0.18),
    0.27
  );
  const darkTopbarStart = ensureDarkTone(
    isDark ? mix(bgBase, brand, 0.18) : mix('#101525', brand, 0.22),
    0.27
  );
  const darkTopbarEnd = ensureDarkTone(
    isDark ? mix(bgBase, accent, 0.22) : mix('#181f32', accent, 0.2),
    0.29
  );

  return {
    '--nav-light-fg': 'var(--text-main)',
    '--nav-light-muted': 'var(--text-soft)',
    '--nav-light-surface': 'var(--surface-1)',
    '--nav-light-surface-border': 'var(--shell-border)',
    '--nav-light-icon-bg': 'var(--workspace-icon-bg)',
    '--nav-light-hover-bg': 'var(--shell-pill-bg)',
    '--nav-light-active-bg': 'var(--nav-selected-bg)',
    '--nav-light-active-border': 'var(--nav-selected-border)',
    '--nav-light-active-text': 'var(--brand-strong)',
    '--nav-light-control-bg': 'var(--surface-1)',
    '--nav-light-control-border': 'var(--shell-border)',
    '--nav-light-utility-bg': 'var(--surface-2)',
    '--nav-light-utility-border': 'var(--shell-border)',
    '--nav-light-profile-bg': 'var(--surface-1)',
    '--nav-light-profile-border': 'var(--shell-border)',
    '--nav-light-bg': 'linear-gradient(180deg, var(--surface-1), var(--surface-2))',
    '--nav-dark-base-start': darkShellStart,
    '--nav-dark-base-end': darkShellEnd,
    '--nav-dark-topbar-start': darkTopbarStart,
    '--nav-dark-topbar-end': darkTopbarEnd,
    '--nav-dark-fg': 'rgba(255, 255, 255, 0.94)',
    '--nav-dark-muted': 'rgba(255, 255, 255, 0.72)',
    '--nav-dark-surface': 'rgba(255, 255, 255, 0.07)',
    '--nav-dark-surface-border': 'rgba(255, 255, 255, 0.1)',
    '--nav-dark-icon-bg': rgba(brand, isDark ? 0.18 : 0.2),
    '--nav-dark-hover-bg': 'rgba(255, 255, 255, 0.08)',
    '--nav-dark-active-bg': gradient(
      '135deg',
      rgba(brand, isDark ? 0.32 : 0.9),
      rgba(accent, isDark ? 0.24 : 0.78)
    ),
    '--nav-dark-active-border': rgba(brand, isDark ? 0.34 : 0.28),
    '--nav-dark-control-bg': 'rgba(255, 255, 255, 0.08)',
    '--nav-dark-control-border': 'rgba(255, 255, 255, 0.12)',
    '--nav-dark-utility-bg': 'rgba(255, 255, 255, 0.06)',
    '--nav-dark-utility-border': 'rgba(255, 255, 255, 0.1)',
    '--nav-dark-profile-bg': 'rgba(255, 255, 255, 0.08)',
    '--nav-dark-profile-border': 'rgba(255, 255, 255, 0.1)',
    '--nav-dark-bg': `radial-gradient(circle at top left, ${rgba(brand, isDark ? 0.18 : 0.22)}, transparent 30%), radial-gradient(circle at bottom right, ${rgba(accent, isDark ? 0.14 : 0.18)}, transparent 34%), ${gradient(
      '180deg',
      rgba(darkShellStart, 0.98),
      rgba(darkShellEnd, 0.98)
    )}`,
    '--nav-dark-topbar-bg': gradient(
      '90deg',
      rgba(darkTopbarStart, 0.96),
      rgba(darkTopbarEnd, 0.94)
    ),
    '--nav-dark-border': rgba(brand, isDark ? 0.24 : 0.18),
    '--nav-dark-kicker': rgba(brand, isDark ? 0.68 : 0.82),
  };
}

function buildDerivedPresetTokens(preset) {
  return {
    ...buildNavigationToneTokens(preset),
    '--table-mobile-card-bg': 'var(--table-surface)',
    '--table-mobile-card-border': 'var(--table-border)',
    '--table-selection-column-bg': 'var(--table-surface)',
  };
}

function withDerivedPresetTokens(preset) {
  return {
    ...preset,
    cssVars: {
      ...buildDerivedPresetTokens(preset),
      ...preset.cssVars,
    },
  };
}

function createColorTheme({
  id,
  label,
  description,
  mode,
  preview,
  fontDisplay,
  fontBody,
  bgBase,
  surface1,
  surface2,
  surface3,
  textMain,
  textSoft,
  textSubtle,
  brand,
  brandStrong,
  accent,
  accentSoft,
  success,
  warning,
  danger,
  borderRadius = 14,
}) {
  const isDark = mode === 'dark';
  const resolvedBrandStrong =
    brandStrong || mix(brand, isDark ? '#ffffff' : '#111827', isDark ? 0.36 : 0.22);
  const resolvedAccentSoft = accentSoft || rgba(accent, isDark ? 0.22 : 0.16);
  const shellBorder = rgba(textMain, isDark ? 0.16 : 0.08);
  const glassBorder = rgba(isDark ? '#ffffff' : textMain, isDark ? 0.14 : 0.08);
  const elevatedBorder = rgba(brand, isDark ? 0.26 : 0.16);
  const surface1Soft = rgba(surface1, 0.98);
  const surface2Soft = rgba(surface2, 0.96);
  const surface3Soft = rgba(surface3, 0.9);
  const primaryActionBg = gradient('135deg', resolvedBrandStrong, brand);
  const primaryActionHoverBg = gradient(
    '135deg',
    mix(resolvedBrandStrong, isDark ? '#0b1014' : '#111827', isDark ? 0.16 : 0.12),
    mix(brand, isDark ? '#0b1014' : '#111827', isDark ? 0.1 : 0.08)
  );
  const sidebarBg = gradient(
    '180deg',
    rgba(mix(bgBase, brand, isDark ? 0.08 : 0.04), 0.98),
    rgba(mix(surface1, accent, isDark ? 0.08 : 0.04), 0.98)
  );
  const bannerBg = gradient(
    '120deg',
    rgba(mix(surface1, brand, isDark ? 0.1 : 0.05), 0.98),
    rgba(mix(surface2, accent, isDark ? 0.12 : 0.06), 0.96)
  );
  const keyboardBannerBg = gradient(
    '118deg',
    rgba(mix(surface1, brand, isDark ? 0.14 : 0.08), 0.96),
    rgba(mix(surface2, accent, isDark ? 0.14 : 0.08), 0.94)
  );
  const selectedBg = gradient(
    '100deg',
    rgba(brand, isDark ? 0.22 : 0.16),
    rgba(accent, isDark ? 0.16 : 0.12)
  );
  const topbarBg = gradient('90deg', rgba(surface1, 0.92), rgba(surface2, 0.92));
  const asideBg = isDark ? rgba(surface2, 0.68) : rgba(mix(surface2, brand, 0.08), 0.82);
  const statAccentBg = gradient(
    '145deg',
    rgba(mix(surface1, brand, 0.08), 0.98),
    rgba(mix(surface2, accent, 0.08), 0.96)
  );
  const documentAccentBg = gradient(
    '135deg',
    rgba(mix(surface1, brand, 0.1), 0.96),
    rgba(mix(surface1, accent, 0.08), 0.92)
  );
  const editorMainBg = gradient(
    '135deg',
    rgba(mix(surface1, brand, isDark ? 0.1 : 0.06), 0.98),
    rgba(mix(surface2, accent, isDark ? 0.1 : 0.06), 0.96)
  );
  const editorHeadBg = gradient(
    '135deg',
    mix(bgBase, brand, isDark ? 0.14 : 0.2),
    mix(surface2, accent, isDark ? 0.2 : 0.24)
  );

  return {
    id,
    label,
    description,
    mode,
    preview,
    antd: {
      colorPrimary: brand,
      colorSuccess: success,
      colorWarning: warning,
      colorError: danger,
      colorBgBase: bgBase,
      colorTextBase: textMain,
      colorBorder: elevatedBorder,
      fontFamily: fontBody,
      borderRadius,
    },
    cssVars: {
      '--font-display': fontDisplay,
      '--font-body': fontBody,
      '--surface-1': surface1Soft,
      '--surface-2': surface2Soft,
      '--surface-3': surface3Soft,
      '--text-main': textMain,
      '--text-soft': textSoft,
      '--text-subtle': textSubtle,
      '--brand': brand,
      '--brand-strong': resolvedBrandStrong,
      '--accent': accent,
      '--accent-soft': resolvedAccentSoft,
      '--success': success,
      '--warning': warning,
      '--danger': danger,
      '--shadow-soft': isDark
        ? '0 18px 42px rgba(0, 0, 0, 0.34)'
        : '0 12px 30px rgba(15, 23, 42, 0.08)',
      '--shadow-elevated': isDark
        ? '0 28px 58px rgba(0, 0, 0, 0.42)'
        : '0 24px 48px rgba(15, 23, 42, 0.12)',
      '--app-bg-base': bgBase,
      '--app-bg-orb-1': rgba(brand, isDark ? 0.18 : 0.14),
      '--app-bg-orb-2': rgba(accent, isDark ? 0.16 : 0.12),
      '--selection-bg': rgba(brand, isDark ? 0.22 : 0.12),
      '--selection-text': textMain,
      '--shell-surface': rgba(surface1, 0.96),
      '--shell-border': shellBorder,
      '--shell-content-bg-start': rgba(surface1, 0.99),
      '--shell-content-bg-end': rgba(surface2, 0.97),
      '--shell-banner-bg': bannerBg,
      '--shell-banner-border': elevatedBorder,
      '--keyboard-banner-bg': keyboardBannerBg,
      '--keyboard-banner-border': rgba(accent, isDark ? 0.24 : 0.18),
      '--section-kicker': resolvedBrandStrong,
      '--sidebar-bg': sidebarBg,
      '--sidebar-border': elevatedBorder,
      '--sidebar-fg': textMain,
      '--sidebar-muted': textSoft,
      '--sidebar-surface': isDark ? 'rgba(255, 255, 255, 0.06)' : rgba(surface1, 0.72),
      '--sidebar-surface-border': glassBorder,
      '--sidebar-icon-bg': isDark ? rgba(brand, 0.16) : rgba(brand, 0.08),
      '--sidebar-action-bg': isDark ? 'rgba(255, 255, 255, 0.06)' : rgba(surface1, 0.94),
      '--sidebar-action-border': glassBorder,
      '--nav-group-text': textSubtle,
      '--nav-hover-bg': isDark ? 'rgba(255, 255, 255, 0.08)' : rgba(brand, 0.06),
      '--nav-selected-bg': selectedBg,
      '--nav-selected-border': rgba(brand, isDark ? 0.28 : 0.18),
      '--topbar-bg': topbarBg,
      '--topbar-border': shellBorder,
      '--topbar-search-bg': isDark ? rgba(surface2, 0.92) : rgba(surface1, 0.96),
      '--topbar-search-border': elevatedBorder,
      '--topbar-control-bg': isDark ? 'rgba(255, 255, 255, 0.06)' : rgba(surface1, 0.92),
      '--topbar-control-border': glassBorder,
      '--shell-pill-bg': isDark ? rgba(surface2, 0.88) : rgba(surface1, 0.94),
      '--shell-pill-border': glassBorder,
      '--shell-key-bg': isDark ? mix(surface2, bgBase, 0.35) : mix(surface2, '#ffffff', 0.55),
      '--shell-key-border': glassBorder,
      '--mode-tag-bg': isDark ? rgba(brand, 0.16) : rgba(brand, 0.1),
      '--mode-tag-border': rgba(brand, isDark ? 0.28 : 0.16),
      '--assist-bg': gradient('180deg', rgba(surface1, 0.96), rgba(surface2, 0.96)),
      '--assist-tag-bg': isDark ? rgba(brand, 0.14) : rgba(brand, 0.08),
      '--assist-tag-border': rgba(brand, isDark ? 0.24 : 0.16),
      '--table-head-bg': isDark ? rgba(surface2, 0.96) : rgba(mix(surface2, brand, 0.08), 0.96),
      '--table-head-bg-2': rgba(surface1, 0.98),
      '--table-header-text': textSubtle,
      '--table-surface': rgba(surface1, 0.98),
      '--table-surface-alt': isDark ? rgba(surface2, 0.94) : rgba(surface2, 0.82),
      '--table-row-hover': isDark ? rgba(brand, 0.14) : rgba(brand, 0.05),
      '--table-active-border': rgba(brand, isDark ? 0.38 : 0.22),
      '--table-border': shellBorder,
      '--workspace-stat-bg': gradient('145deg', rgba(surface1, 0.98), rgba(surface2, 0.96)),
      '--workspace-stat-accent-bg': statAccentBg,
      '--workspace-toolbar-bg': isDark ? rgba(surface2, 0.9) : rgba(surface1, 0.88),
      '--workspace-panel-bg-start': rgba(surface1, 0.98),
      '--workspace-panel-bg-end': rgba(surface2, 0.96),
      '--workspace-aside-bg': asideBg,
      '--workspace-aside-border': rgba(brand, isDark ? 0.18 : 0.12),
      '--workspace-keyboard-hero-bg': bannerBg,
      '--workspace-keyboard-hero-border': rgba(accent, isDark ? 0.22 : 0.16),
      '--workspace-keyboard-pill-bg': isDark ? rgba(surface2, 0.92) : rgba(surface1, 0.92),
      '--workspace-keyboard-pill-border': glassBorder,
      '--workspace-keyboard-key-bg': isDark ? mix(surface2, bgBase, 0.35) : mix(surface2, '#ffffff', 0.55),
      '--workspace-keyboard-key-border': glassBorder,
      '--workspace-keyboard-item-bg': isDark ? rgba(surface2, 0.92) : rgba(surface1, 0.92),
      '--workspace-keyboard-item-active-bg': statAccentBg,
      '--workspace-keyboard-item-active-border': rgba(brand, isDark ? 0.3 : 0.18),
      '--workspace-keyboard-item-active-ring': rgba(brand, isDark ? 0.12 : 0.08),
      '--workspace-icon-bg': isDark ? rgba(brand, 0.16) : rgba(brand, 0.1),
      '--workspace-meta-pill-bg': isDark ? rgba(surface2, 0.96) : rgba(surface2, 0.9),
      '--workspace-meta-pill-border': glassBorder,
      '--document-meta-bg': documentAccentBg,
      '--document-meta-border': rgba(brand, isDark ? 0.2 : 0.14),
      '--document-section-border': shellBorder,
      '--document-section-bg-start': rgba(surface1, 0.98),
      '--document-section-bg-end': rgba(surface2, 0.96),
      '--document-preview-bg': documentAccentBg,
      '--document-preview-border': rgba(brand, isDark ? 0.18 : 0.14),
      '--document-preview-label': textSubtle,
      '--document-item-bg-start': rgba(surface2, 0.96),
      '--document-item-bg-end': rgba(surface1, 0.98),
      '--document-item-border': shellBorder,
      '--document-neutral-card-bg': rgba(surface1, 0.96),
      '--document-neutral-card-border': shellBorder,
      '--document-chip-bg': isDark ? rgba(accent, 0.14) : resolvedAccentSoft,
      '--document-chip-border': rgba(accent, isDark ? 0.22 : 0.16),
      '--document-add-bg': isDark ? rgba(brand, 0.1) : rgba(brand, 0.12),
      '--document-summary-hero-bg': documentAccentBg,
      '--document-summary-hero-border': rgba(success, isDark ? 0.18 : 0.14),
      '--document-summary-row-border': shellBorder,
      '--editor-band-border': shellBorder,
      '--editor-band-main-bg': editorMainBg,
      '--editor-band-aside-bg': gradient('180deg', rgba(surface1, 0.98), rgba(surface2, 0.96)),
      '--editor-chip-bg': isDark ? rgba(surface2, 0.92) : rgba(surface1, 0.88),
      '--editor-chip-border': rgba(brand, isDark ? 0.22 : 0.16),
      '--editor-chip-text': resolvedBrandStrong,
      '--editor-field-cluster-bg': isDark ? rgba(surface2, 0.88) : rgba(surface2, 0.82),
      '--editor-field-cluster-border': shellBorder,
      '--editor-board-bg': rgba(surface1, 0.96),
      '--editor-board-border': shellBorder,
      '--editor-board-head-bg': editorHeadBg,
      '--editor-board-head-text': '#ffffff',
      '--editor-mobile-bar-bg': isDark ? rgba(surface1, 0.94) : rgba(surface1, 0.94),
      '--editor-mobile-bar-border': shellBorder,
      '--primary-action-bg': primaryActionBg,
      '--primary-action-hover-bg': primaryActionHoverBg,
      '--switch-handle-bg': isDark ? rgba(surface1, 0.98) : '#ffffff',
    },
  };
}

Object.assign(designPresets, {
  sageLedger: createColorTheme({
    id: 'sageLedger',
    label: 'Sage Ledger',
    description:
      'A calm green-and-paper palette inspired by softer Color Hunt pastel and nature collections.',
    mode: 'light',
    preview: {
      hero: '#eef5e8',
      surface: '#ffffff',
      accent: '#5e936c',
      accentSoft: '#d9ead3',
    },
    fontDisplay: "'Aptos Display', 'Segoe UI', sans-serif",
    fontBody: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
    bgBase: '#f6fbf4',
    surface1: '#ffffff',
    surface2: '#eef5e8',
    surface3: '#dce8d4',
    textMain: '#223127',
    textSoft: '#5a6b61',
    textSubtle: '#7a8b82',
    brand: '#5e936c',
    brandStrong: '#3f6f50',
    accent: '#a0c878',
    accentSoft: '#dff0d8',
    success: '#3d8d5a',
    warning: '#d69a3b',
    danger: '#c85b5b',
    borderRadius: 16,
  }),
  harborNight: createColorTheme({
    id: 'harborNight',
    label: 'Harbor Night',
    description:
      'A navy, teal, and brass palette adapted from darker Color Hunt collections like gold-teal-navy.',
    mode: 'dark',
    preview: {
      hero: '#16212c',
      surface: '#0f1720',
      accent: '#d6a75e',
      accentSoft: '#21424b',
    },
    fontDisplay: "'Georgia', 'Cambria', 'Times New Roman', serif",
    fontBody: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
    bgBase: '#091018',
    surface1: '#0f1720',
    surface2: '#16212c',
    surface3: '#233647',
    textMain: '#edf4f7',
    textSoft: '#afc0c7',
    textSubtle: '#8fa3ac',
    brand: '#d6a75e',
    brandStrong: '#edd09a',
    accent: '#58a6a6',
    accentSoft: '#21424b',
    success: '#5ca67b',
    warning: '#d6a75e',
    danger: '#d46b6b',
    borderRadius: 14,
  }),
  copperDawn: createColorTheme({
    id: 'copperDawn',
    label: 'Copper Dawn',
    description:
      'A warm copper-and-sky palette inspired by brighter Color Hunt cream and blue combinations.',
    mode: 'light',
    preview: {
      hero: '#f7eadf',
      surface: '#fffdf8',
      accent: '#c87e4f',
      accentSoft: '#d8e7f2',
    },
    fontDisplay: "'Trebuchet MS', 'Aptos Display', 'Segoe UI', sans-serif",
    fontBody: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
    bgBase: '#fff9f1',
    surface1: '#fffdf8',
    surface2: '#f7eadf',
    surface3: '#d8e7f2',
    textMain: '#2f2f33',
    textSoft: '#666873',
    textSubtle: '#8a8e99',
    brand: '#c87e4f',
    brandStrong: '#9e5b33',
    accent: '#5b88a5',
    accentSoft: '#d8e7f2',
    success: '#3f8c73',
    warning: '#d59b46',
    danger: '#c65d52',
    borderRadius: 16,
  }),
  roseQuartz: createColorTheme({
    id: 'roseQuartz',
    label: 'Rose Quartz',
    description:
      'A soft rose-and-indigo light palette adapted from Color Hunt pastel combinations for calmer billing review workflows.',
    mode: 'light',
    preview: {
      hero: '#fdf4f8',
      surface: '#ffffff',
      accent: '#d16a8a',
      accentSoft: '#d8d9ff',
    },
    fontDisplay: "'Aptos Display', 'Segoe UI', sans-serif",
    fontBody: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
    bgBase: '#fff8fb',
    surface1: '#ffffff',
    surface2: '#fdf4f8',
    surface3: '#eef1ff',
    textMain: '#2c2634',
    textSoft: '#65607a',
    textSubtle: '#8f88a5',
    brand: '#d16a8a',
    brandStrong: '#a64c6f',
    accent: '#7d78d8',
    accentSoft: '#e5e7ff',
    success: '#4d9a74',
    warning: '#d99541',
    danger: '#d45d65',
    borderRadius: 16,
  }),
  midnightEmber: createColorTheme({
    id: 'midnightEmber',
    label: 'Midnight Ember',
    description:
      'A charcoal, ember, and cyan dark palette inspired by deeper Color Hunt contrast sets for operator-heavy teams.',
    mode: 'dark',
    preview: {
      hero: '#151a23',
      surface: '#0d1219',
      accent: '#f0b27a',
      accentSoft: '#214455',
    },
    fontDisplay: "'Georgia', 'Cambria', 'Times New Roman', serif",
    fontBody: "'Aptos', 'Segoe UI Variable Text', 'Segoe UI', sans-serif",
    bgBase: '#080d13',
    surface1: '#0d1219',
    surface2: '#151a23',
    surface3: '#243241',
    textMain: '#edf3f8',
    textSoft: '#afbecb',
    textSubtle: '#8da0ad',
    brand: '#f0b27a',
    brandStrong: '#ffd6ad',
    accent: '#58c0c7',
    accentSoft: '#214455',
    success: '#6ab38a',
    warning: '#e0a661',
    danger: '#d97777',
    borderRadius: 14,
  }),
});

Object.keys(designPresets).forEach((presetId) => {
  designPresets[presetId] = withDerivedPresetTokens(designPresets[presetId]);
});

export function getDesignPreset(presetId) {
  return designPresets[presetId] || designPresets[DEFAULT_DESIGN_PRESET];
}

export const designPresetList = Object.values(designPresets);
